import { adaptLegacyColumns } from './adaptColumns';
import { createContext, useContext, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getExpandedRowModel,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  type ExpandedState,
  type PaginationState,
  type Table,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { type DataTableContextType, type LegacyColumn, isLegacyColumns } from './types';

const DataTableContext = createContext<DataTableContextType<unknown> | null>(null);

export function useDataTable<TData>() {
  const context = useContext(DataTableContext) as DataTableContextType<TData> | null;
  if (!context) {
    throw new Error('useDataTable must be used within a DataTableProvider');
  }
  return context;
}

interface DataTableProviderProps<TData> {
  columns: ColumnDef<TData, unknown>[] | LegacyColumn<TData>[];
  data: TData[];
  children: ReactNode;
  // Optional controlled state
  pagination?: boolean;
  pageSize?: number;
  manualPagination?: boolean;
  pageCount?: number;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  getRowId?: (row: TData) => string;
  enableColumnResizing?: boolean;
}

export function DataTableProvider<TData>({
  columns: rawColumns,
  data,
  children,
  pagination = true,
  pageSize = 10,
  manualPagination = false,
  pageCount,
  onPaginationChange,
  getRowId,
  enableColumnResizing = false,
}: DataTableProviderProps<TData>) {
  const columns = isLegacyColumns(rawColumns) ? adaptLegacyColumns(rawColumns) : rawColumns;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<TData, any>[],
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      expanded,
      ...(pagination ? { pagination: paginationState } : {}),
    },
    enableColumnResizing,
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(paginationState) : updater;
      setPaginationState(newState);
      onPaginationChange?.(newState.pageIndex, newState.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(manualPagination ? { manualPagination: true, pageCount } : {}),
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
  });

  return (
    <DataTableContext.Provider value={{ table: table as Table<unknown> }}>
      {children}
    </DataTableContext.Provider>
  );
}
